import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Settings, Users, Star, Gift, Plus, Crown, Shield, User, Trash2, Copy, Calendar, Search, TrendingUp, DollarSign, Activity } from 'lucide-react';
import { getBackendUrl, getApiUrl } from '../utils/config';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

const AdminDashboard = ({ user, onBack }) => {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total_users: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  useEffect(() => {
    fetchAdminData();
  }, [currentPage, pageSize]); // Refetch when page or page size changes

  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Make API calls sequentially to avoid race conditions
      console.log('Fetching admin data...');
      
      const usersRes = await axios.get(`${API}/admin/users`, { 
        headers,
        params: {
          page: currentPage,
          limit: pageSize
        }
      });
      console.log('Users fetched successfully');
      setUsers(usersRes.data.users || []);
      setPagination(usersRes.data.pagination || pagination);
      
      const plansRes = await axios.get(`${API}/admin/subscription-plans`, { headers });
      console.log('Plans fetched successfully');
      setPlans(plansRes.data || []);
      
      const promoRes = await axios.get(`${API}/admin/promo-codes`, { headers });
      console.log('Promo codes fetched successfully');
      setPromoCodes(promoRes.data || []);
      
      const analyticsRes = await axios.get(`${API}/admin/subscription-analytics`, { headers });
      console.log('Analytics fetched successfully');
      setSubscriptionAnalytics(analyticsRes.data || null);
      
      console.log('All admin data fetched successfully');
      
    } catch (error) {
      console.error('Error fetching admin data:', error);
      
      // Set default values to prevent crashes
      setUsers([]);
      setPlans([]);
      setPromoCodes([]);
      setSubscriptionAnalytics(null);
      
      // More specific error handling
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Request timeout. Please try again.');
      } else {
        toast.error(`Failed to load admin data: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [API, currentPage, pageSize]);

  const handleUpdateUserRole = useCallback(async (userId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { headers });
      toast.success('User role updated successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Update user role error:', error);
      toast.error('Failed to update user role');
    }
  }, [API, fetchAdminData]);

  // ADMIN PASSWORD RESET FUNCTION - Simple, robust implementation  
  const resetUserPassword = useCallback(async (userId, userName, userEmail) => {
    // Step 1: Get new password
    const newPassword = window.prompt(
      `🔑 PASSWORD RESET\n\n` +
      `Reset password for: ${userName} (${userEmail})\n\n` +
      `Enter new password (minimum 6 characters):`
    );
    
    if (!newPassword) {
      console.log('Password reset cancelled by admin');
      return;
    }
    
    if (newPassword.length < 6) {
      alert('❌ Password must be at least 6 characters long');
      return;
    }

    // Step 2: Confirmation
    const confirmReset = window.confirm(
      `🔐 CONFIRM PASSWORD RESET\n\n` +
      `User: ${userName} (${userEmail})\n` +
      `New Password: ${newPassword}\n\n` +
      `Reset password for this user?`
    );
    
    if (!confirmReset) {
      console.log('Password reset confirmation cancelled');
      return;
    }

    try {
      // Step 3: Get authentication token
      const token = localStorage.getItem('token');
      console.log('Password reset request for user:', userId, 'by admin with token:', !!token);
      
      if (!token) {
        alert('ERROR: No authentication token found. Please login again.');
        return;
      }

      // Step 4: Show loading state
      toast('Resetting password...', { description: 'Please wait' });

      // Step 5: Make API call
      console.log('Making password reset request to:', `${API}/admin/users/${userId}/reset-password`);
      
      const response = await fetch(`${API}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      console.log('Password reset response status:', response.status);

      // Step 6: Handle response
      if (response.ok) {
        const result = await response.json();
        console.log('Password reset successful:', result);
        toast.success(`✅ Password reset successfully for ${userName}`);
      } else {
        const errorText = await response.text();
        console.error('Password reset failed:', response.status, errorText);
        
        if (response.status === 403) {
          toast.error('Access denied: You do not have permission to reset passwords');
        } else if (response.status === 404) {
          toast.error('User not found');
        } else {
          toast.error(`Password reset failed: ${errorText || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(`Error resetting password: ${error.message}`);
    }
  }, [API]);

  // ADMIN USER DELETION FUNCTION - Simple, robust implementation
  const deleteUser = useCallback(async (userId, userName, userEmail) => {
    // Step 1: Confirmation
    const confirmDelete = window.confirm(
      `⚠️ PERMANENT DELETION WARNING ⚠️\n\n` +
      `Delete user: ${userName} (${userEmail})?\n\n` +
      `This will permanently remove:\n` +
      `• User account and profile\n` +
      `• All cars and vehicle data\n` +
      `• All service records\n` +
      `• All parts lists\n` +
      `• All photos and files\n\n` +
      `THIS CANNOT BE UNDONE!\n\n` +
      `Click OK to permanently delete this user.`
    );
    
    if (!confirmDelete) {
      console.log('User deletion cancelled by admin');
      return;
    }

    // Step 2: Final confirmation
    const finalConfirm = window.confirm(
      `FINAL CONFIRMATION\n\n` +
      `You are about to permanently delete:\n` +
      `${userName} (${userEmail})\n\n` +
      `Are you absolutely sure?`
    );
    
    if (!finalConfirm) {
      console.log('Final confirmation cancelled');
      return;
    }

    try {
      // Step 3: Get authentication token
      const token = localStorage.getItem('token');
      console.log('Admin deletion request for user:', userId, 'by admin with token:', !!token);
      
      if (!token) {
        alert('ERROR: No authentication token found. Please login again.');
        return;
      }

      // Step 4: Show loading state
      toast('Deleting user...', { description: 'This may take a moment' });

      // Step 5: Make API call
      console.log('Making DELETE request to:', `${API}/admin/users/${userId}`);
      
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Delete response status:', response.status);

      // Step 6: Handle response
      if (response.ok) {
        console.log('User deletion successful');
        toast.success(`✅ User ${userName} has been permanently deleted`);
        
        // Refresh the user list
        await fetchAdminData();
      } else {
        const errorText = await response.text();
        console.error('Delete failed:', response.status, errorText);
        
        if (response.status === 403) {
          toast.error('Access denied: You do not have permission to delete users');
        } else if (response.status === 404) {
          toast.error('User not found - may have already been deleted');
          await fetchAdminData(); // Refresh list
        } else {
          toast.error(`Deletion failed: ${errorText || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(`Error deleting user: ${error.message}`);
    }
  }, [API, fetchAdminData]);

  const handleCreatePromoCode = async (codeData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      await axios.post(`${API}/admin/promo-codes`, codeData, { headers });
      toast.success('Promo code created successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Create promo code error:', error);
      toast.error('Failed to create promo code');
    }
  };

  const handleDeactivatePromoCode = async (codeId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      await axios.delete(`${API}/admin/promo-codes/${codeId}`, { headers });
      toast.success('Promo code deactivated');
      fetchAdminData();
    } catch (error) {
      console.error('Deactivate promo code error:', error);
      toast.error('Failed to deactivate promo code');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'admin': return <Shield className="w-4 h-4 text-blue-600" />;
      default: return <User className="w-4 h-4 text-gray-600" />;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner': return <Badge className="bg-yellow-100 text-yellow-800">Owner</Badge>;
      case 'admin': return <Badge className="bg-blue-100 text-blue-800">Admin</Badge>;
      default: return <Badge variant="secondary">User</Badge>;
    }
  };

  // Filter users based on search term and role
  const filteredUsers = users.filter(userItem => {
    const matchesSearch = userItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         userItem.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || userItem.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Settings className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="w-6 h-6 mr-2 text-blue-600" />
            Admin Dashboard
          </h2>
          <p className="text-gray-600 mt-1">Manage users, subscriptions, and promo codes</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
              🔑 Password Reset: {(user?.role === 'admin' || user?.role === 'owner') ? 'Available' : 'Admin+ Only'}
            </span>
            <span className="text-sm text-red-600 bg-red-50 px-2 py-1 rounded">
              🗑️ User Deletion: {(user?.role === 'owner') ? 'Available' : 'Owner Only'}
            </span>
          </div>
        </div>
        <Button variant="outline" onClick={onBack} data-testid="back-to-dashboard">
          ← Back to Dashboard
        </Button>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="promo-codes">Promo Codes</TabsTrigger>
        </TabsList>

        {/* Users Management */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2 text-blue-600" />
                  User Management
                </div>
                <div className="text-sm text-gray-600">
                  Showing {filteredUsers.length} of {pagination.total_users} total users
                </div>
              </CardTitle>
              <CardDescription>
                Manage user accounts and permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search, Filter and Pagination Controls */}
              <div className="flex gap-4 mb-6 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    placeholder="Search users by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-md"
                  />
                </div>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="user">Users</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                    <SelectItem value="owner">Owners</SelectItem>
                  </SelectContent>
                </Select>
                <Select 
                  value={pageSize.toString()} 
                  onValueChange={(value) => {
                    setPageSize(parseInt(value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Per page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Users List */}
              <div className="space-y-4">
                {filteredUsers.map((userItem) => (
                  <div key={userItem.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        {getRoleIcon(userItem.role)}
                        <div className="ml-2">
                          <div className="font-medium">{userItem.name}</div>
                          <div className="text-sm text-gray-500">{userItem.email}</div>
                          <div className="text-xs text-gray-400">
                            Joined: {new Date(userItem.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      {getRoleBadge(userItem.role)}
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="text-sm">
                        <div className="font-medium">{userItem.subscription.plan_name}</div>
                        <div className="text-gray-500">
                          {userItem.subscription.max_cars === -1 ? '∞' : userItem.subscription.max_cars} cars
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {(user?.role === 'admin' || user?.role === 'owner') && userItem.id !== user.id && (
                          <Select
                            value={userItem.role}
                            onValueChange={(newRole) => handleUpdateUserRole(userItem.id, newRole)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="owner">Owner</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        
                        {/* Admin Functions - Simple & Reliable */}
                        <div className="flex gap-2">
                          {(user?.role === 'admin' || user?.role === 'owner') && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-700 border-blue-300 hover:bg-blue-50 hover:border-blue-400"
                              onClick={() => resetUserPassword(userItem.id, userItem.name, userItem.email)}
                              title={`Reset password for ${userItem.name}`}
                            >
                              🔑 Reset
                            </Button>
                          )}
                          
                          {(user?.role === 'owner') && userItem.id !== user.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-700 border-red-300 hover:bg-red-50 hover:border-red-400"
                              onClick={() => deleteUser(userItem.id, userItem.name, userItem.email)}
                              title={`Delete ${userItem.name} permanently`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm || selectedRole !== 'all' ? 
                      'No users match your search criteria' : 
                      'No users found'
                    }
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.total_pages} 
                  <span className="ml-2">
                    ({((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total_users)} of {pagination.total_users} users)
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={!pagination.has_prev}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!pagination.has_prev}
                  >
                    ← Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!pagination.has_next}
                  >
                    Next →
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(pagination.total_pages)}
                    disabled={!pagination.has_next}
                  >
                    Last
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Analytics */}
        <TabsContent value="subscriptions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Star className="w-5 h-5 mr-2 text-green-600" />
                Subscription Analytics
              </CardTitle>
              <CardDescription>
                Monitor subscription metrics and revenue
              </CardDescription>
            </CardHeader>
            <CardContent>
              {subscriptionAnalytics && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-blue-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {subscriptionAnalytics.summary.total_users}
                        </div>
                        <div className="text-sm text-blue-700">Total Users</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-green-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {subscriptionAnalytics.summary.active_subscriptions}
                        </div>
                        <div className="text-sm text-green-700">Active Subscriptions</div>
                        <div className="text-xs text-green-600">
                          {subscriptionAnalytics.summary.subscription_rate}% conversion
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-purple-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          ${subscriptionAnalytics.summary.total_monthly_revenue}
                        </div>
                        <div className="text-sm text-purple-700">Monthly Revenue</div>
                        <div className="text-xs text-purple-600">
                          ${subscriptionAnalytics.summary.avg_revenue_per_user}/user avg
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-orange-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-orange-600">
                          {subscriptionAnalytics.summary.recent_signups}
                        </div>
                        <div className="text-sm text-orange-700">New Users (30d)</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Plan Distribution */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Plan Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(subscriptionAnalytics.plan_distribution).map(([plan, count]) => (
                            <div key={plan} className="flex justify-between items-center">
                              <span className="font-medium">{plan}</span>
                              <div className="flex items-center space-x-2">
                                <Badge variant="outline">{count} users</Badge>
                                <div className="text-sm text-gray-500">
                                  {((count / subscriptionAnalytics.summary.total_users) * 100).toFixed(1)}%
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Recent User Activity */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Recent Users</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {subscriptionAnalytics.user_details.slice(0, 10).map((userDetail) => (
                            <div key={userDetail.user_id} className="flex justify-between items-center text-sm">
                              <div>
                                <div className="font-medium">{userDetail.name}</div>
                                <div className="text-gray-500">{userDetail.plan_name}</div>
                              </div>
                              <div className="text-right">
                                <Badge 
                                  className={
                                    userDetail.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                                    userDetail.subscription_status === 'expired' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }
                                >
                                  {userDetail.subscription_status}
                                </Badge>
                                {userDetail.monthly_revenue > 0 && (
                                  <div className="text-xs text-green-600 mt-1">
                                    ${userDetail.monthly_revenue}/mo
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Plans */}
        <TabsContent value="plans" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Star className="w-5 h-5 mr-2 text-purple-600" />
                Subscription Plans
              </CardTitle>
              <CardDescription>
                Manage subscription plans and pricing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((plan) => (
                  <Card key={plan.id} className="border-2">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        {plan.name}
                        <Badge variant={plan.is_active ? "default" : "secondary"}>
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </CardTitle>
                      <CardDescription>{plan.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Price:</span>
                          <span className="font-semibold">${plan.price}/month</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Max Cars:</span>
                          <span>{plan.max_cars === -1 ? 'Unlimited' : plan.max_cars}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Duration:</span>
                          <span>{plan.duration_months} month(s)</span>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Label className="text-xs font-medium">Features:</Label>
                        <ul className="text-xs text-gray-600 mt-1">
                          {plan.features.map((feature, idx) => (
                            <li key={idx}>• {feature}</li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Promo Codes */}
        <TabsContent value="promo-codes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Gift className="w-5 h-5 mr-2 text-green-600" />
                  Promo Code Management
                </div>
                <PromoCodeCreateDialog onSuccess={fetchAdminData} plans={plans} />
              </CardTitle>
              <CardDescription>
                Create and manage promotional codes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {promoCodes.map((promo) => (
                  <div key={promo.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <div className="font-mono text-lg font-bold bg-gray-100 px-3 py-1 rounded">
                          {promo.code}
                        </div>
                        <div>
                          <div className="font-medium">{promo.name}</div>
                          <div className="text-sm text-gray-500">{promo.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                        <span>Type: {promo.type.replace('_', ' ')}</span>
                        <span>Value: {promo.value}</span>
                        {promo.subscription_plan_id && (
                          <span>
                            Plan: {plans?.find(p => p.id === promo.subscription_plan_id)?.name || 'Unknown'}
                          </span>
                        )}
                        {!promo.subscription_plan_id && (promo.type === 'free_month' || promo.type === 'free_year') && (
                          <span>Plan: Basic (default)</span>
                        )}
                        <span>Uses: {promo.current_uses}/{promo.max_uses || '∞'}</span>
                        {promo.expiry_date && (
                          <span>Expires: {new Date(promo.expiry_date).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Badge variant={promo.is_active ? "default" : "secondary"}>
                        {promo.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(promo.code);
                          toast.success('Promo code copied to clipboard!');
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      {promo.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleDeactivatePromoCode(promo.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Promo Code Creation Dialog
const PromoCodeCreateDialog = ({ onSuccess, plans }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'free_month',
    value: 1,
    subscription_plan_id: 'default',
    max_uses: '',
    expiry_date: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      value: parseFloat(formData.value),
      subscription_plan_id: formData.subscription_plan_id === 'default' ? null : formData.subscription_plan_id || null,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null
    };

    try {
      await axios.post(`${API}/admin/promo-codes`, submitData);
      toast.success('Promo code created successfully!');
      setOpen(false);
      setFormData({
        name: '',
        description: '',
        type: 'free_month',
        value: 1,
        subscription_plan_id: 'default',
        max_uses: '',
        expiry_date: ''
      });
      onSuccess();
    } catch (error) {
      toast.error('Failed to create promo code');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'free_month',
      value: 1,
      subscription_plan_id: 'default',
      max_uses: '',
      expiry_date: ''
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Create Promo Code
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Promo Code</DialogTitle>
          <DialogDescription>
            Generate a new promotional code for users
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Holiday Special 2024"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Special promotion for the holidays"
              rows={2}
              required
            />
          </div>

          {/* Subscription Plan Selection - only show for free subscription types */}
          {(formData.type === 'free_month' || formData.type === 'free_year') && (
            <div className="space-y-2">
              <Label htmlFor="subscription_plan">Subscription Plan to Grant</Label>
              <Select 
                value={formData.subscription_plan_id} 
                onValueChange={(value) => setFormData({...formData, subscription_plan_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select subscription plan (default: Basic)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Basic (default)</SelectItem>
                  {plans && plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} - {plan.max_cars === -1 ? 'Unlimited' : plan.max_cars} cars (${plan.price}/month)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Choose which subscription tier this promo code grants. If not selected, Basic plan will be used.
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free_month">Free Month(s)</SelectItem>
                  <SelectItem value="free_year">Free Year</SelectItem>
                  <SelectItem value="discount_percent">Discount %</SelectItem>
                  <SelectItem value="discount_amount">Discount $</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="value">Value</Label>
              <Input
                id="value"
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                placeholder="1"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="max_uses">Max Uses (optional)</Label>
              <Input
                id="max_uses"
                type="number"
                value={formData.max_uses}
                onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                placeholder="100"
                min="1"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiry_date">Expiry Date (optional)</Label>
              <Input
                id="expiry_date"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
              />
            </div>
          </div>
          
          <Button type="submit" className="w-full">
            Create Promo Code
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;